import React, { useState, useCallback } from 'react';
import { Select, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import debounce from 'lodash/debounce';
import searchPatients from '../../utils/patient/searchPatients';
import { useTranslation } from 'react-i18next';

const MIN_SEARCH_LENGTH_FOR_CREATE = 3;

export default function PatientSelect({
                                        selectedPatient,
                                        onSelect,
                                        onAddNewPatient,
                                        selectStyle = {},
                                        placeholder = null,
                                      }) {
  const { t } = useTranslation();
  const [options, setOptions] = useState([]); // holds only actual patient results
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');

  // Fetch patients matching the query.
  const loadPatientOptions = async (value) => {
    return await searchPatients(value);
  };

  // Debounced search handler.
  const handleSearch = useCallback(
    debounce(async (value) => {
      setSearchText(value);
      if (value.length < 2) {
        setOptions([]);
        setIsOpen(false);
        return;
      }
      setLoading(true);
      try {
        const newOptions = await loadPatientOptions(value);
        setOptions(newOptions);
      } finally {
        setLoading(false);
        // force open dropdown even if no options so that notFoundContent appears.
        setIsOpen(true);
      }
    }, 300),
    [t]
  );

  // Actual patient selection handler.
  const handleChange = ({ value }) => {
    const selected = options.find((p) => p.getUUID() === value);
    if (selected) {
      onSelect(selected);
    }
    setIsOpen(false);
  };

  // Handler for the custom "create new patient" button.
  const handleCreateNew = () => {
    // Clear any existing selection.
    onSelect(null);
    // Reset options and close dropdown.
    setOptions([]);
    setIsOpen(false);
    // Begin the new patient creation flow.
    onAddNewPatient?.();
  };

  // Enhanced notFoundContent styling to appear interactive.
  const customNotFoundContent =
    loading ? (
      <Spin size="small" />
    ) : searchText.length >= MIN_SEARCH_LENGTH_FOR_CREATE && options.length === 0 ? (
      <div
        style={{
          textAlign: 'center',
          fontWeight: 'bold',
          cursor: 'pointer',
          color:  'var(--main-color)', // blue text to denote interactivity
          backgroundColor: '#fff', // white background
          userSelect: 'none'
        }}
        // Prevent default focus/selection behavior.
        onMouseDown={(e) => e.preventDefault()}
        onClick={handleCreateNew}
      >
        {t('patient_selector_panel.create_new_option') ||
          'No user found – create a new one'}
      </div>
    ) : null;

  return (
    <Select
      showSearch
      labelInValue
      value={
        selectedPatient
          ? {
            value: selectedPatient.getUUID(),
            label: selectedPatient.getFullName(),
          }
          : undefined
      }
      placeholder={placeholder}
      filterOption={false}
      onSearch={handleSearch}
      onBlur={() => setIsOpen(false)}
      onChange={handleChange}
      notFoundContent={customNotFoundContent}
      options={options.map((p) => ({
        value: p.getUUID(),
        label: p.getFullName(),
      }))}
      suffixIcon={<SearchOutlined style={{ color: '#999' }} />}
      open={isOpen}
      style={{ fontSize: '16px', width: '100%', ...selectStyle }}
    />
  );
}